import React, { useEffect } from 'react';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { useOnceReveal } from '@common/hooks';

import type { OptionAction, OptionData, OptionUiSetting, PreviewData, RestockButton } from '../../../type/common.type';
import { Content } from './parts/Content';
import { PreviewInfo } from './parts/PreviewInfo';
import { PriceStock } from './parts/PriceStock';
import { ProductInfo } from './parts/ProductInfo';
import { RestockAlertButton } from './parts/RestockAlertButton';

interface SelectBoxItemProps {
  children?: React.ReactNode;
  preview?: PreviewData;
  option: OptionData;
  ui_setting?: OptionUiSetting;
  action?: OptionAction;
  restock_button?: RestockButton;
  displayPrice?: boolean;
  displayRestockButton?: boolean;
}

export const SelectBoxItem = ({
  children,
  preview,
  option,
  ui_setting,
  action,
  restock_button,
  displayPrice = true,
  displayRestockButton = true,
}: SelectBoxItemProps) => {
  const { targetRef, isRevealed } = useOnceReveal();

  const isSoldOut = option.stock_status?.is_sold_out ?? false;
  const displayRestockNotificationSelection = ui_setting?.display_style === 'restock_notification_selection';
  const hasShippingBadge = !!option.shipping?.type;
  const hasPreviewImageOnly = !hasShippingBadge && !!preview?.image_url;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    action?.handleSelect?.(option);
  };

  useEffect(() => {
    if (isRevealed) {
      action?.handleImpress?.(option, !!restock_button?.active);
    }
  }, [isRevealed]);

  // 커스텀 구성인 경우 (children이 있는 경우)
  if (children) {
    return (
      <Container
        ref={targetRef}
        isSoldOut={!displayRestockNotificationSelection && isSoldOut}
        displayShippingBadge={hasShippingBadge}
        hasPreviewImageOnly={hasPreviewImageOnly}
        onClick={handleClick}
      >
        {children}
      </Container>
    );
  }

  // 기본 구성 - 내부적으로 조건부 렌더링 처리
  return (
    <Container
      ref={targetRef}
      isSoldOut={!displayRestockNotificationSelection && isSoldOut}
      displayShippingBadge={hasShippingBadge}
      hasPreviewImageOnly={hasPreviewImageOnly}
      onClick={handleClick}
    >
      {preview && <SelectBoxItem.Preview preview={preview} />}
      <SelectBoxItem.Content>
        <SelectBoxItem.ProductInfo option={option} ui_setting={ui_setting} />

        {isSoldOut && restock_button && displayRestockButton ? (
          <SelectBoxItem.RestockAlertButton option={option} restock_button={restock_button} action={action} />
        ) : (
          displayPrice && <SelectBoxItem.PriceStock option={option} ui_setting={ui_setting} />
        )}
      </SelectBoxItem.Content>
    </Container>
  );
};

SelectBoxItem.Preview = PreviewInfo;
SelectBoxItem.Content = Content;
SelectBoxItem.ProductInfo = ProductInfo;
SelectBoxItem.PriceStock = PriceStock;
SelectBoxItem.RestockAlertButton = RestockAlertButton;

const Container = styled.div<{
  isSoldOut: boolean;
  displayShippingBadge: boolean;
  hasPreviewImageOnly: boolean;
}>`
  --base-padding: 16px;
  --shipping-badge-padding: 17px 16px;
  --preview-image-only-padding: 8px 16px;

  position: relative;
  display: flex;
  align-items: center;
  background: ${({ isSoldOut }) => (isSoldOut ? vars.v3Color.gray50 : vars.color.staticWhite)};
  padding: ${({ displayShippingBadge, hasPreviewImageOnly }) =>
    displayShippingBadge
      ? `var(--shipping-badge-padding)`
      : hasPreviewImageOnly
        ? `var(--preview-image-only-padding)`
        : 'var(--base-padding)'};
`;
